import { useEffect, RefObject, useCallback, useRef } from 'react';

import { UseFormReturn } from 'react-hook-form';

import { useStores } from 'mobx/hooks/useStores';

import Ticket from 'models/Ticket';

type MessagingInputScrollReadyState = {
  isMessageInputFocused?: boolean;
  areMessagesLoaded?: boolean;
};

interface SendMessageForm {
  message: string;
}

/**
 * Custom hook to handle auto-focusing the messaging input field when triggered
 * via the Message Quick Action button.
 *
 * This hook checks if the ticket has been marked for messaging focus in the
 * ticketsStore, and if so, focuses the message input field, scrolls it into view,
 * and resets the flag.
 *
 * @param ticket - The ticket to check for messaging focus
 * @param sendMessageForm - The react-hook-form instance controlling the message input
 * @param inputRef - ref to the textarea input element for scrolling
 */
export const useMessagingFocus = (
  ticket: Ticket,
  sendMessageForm: UseFormReturn<SendMessageForm>,
  inputRef: RefObject<HTMLTextAreaElement>
) => {
  const { ticketsStore } = useStores();
  const shouldFocusMessaging = ticketsStore.messagingFocusMap.get(ticket.id);
  const scrollReadyRef = useRef<MessagingInputScrollReadyState>({});

  const scrollToMessageInput = useCallback(
    ({ isMessageInputFocused, areMessagesLoaded }: MessagingInputScrollReadyState) => {
      const scrollReadyState = scrollReadyRef.current;

      if (isMessageInputFocused !== undefined)
        scrollReadyState.isMessageInputFocused = isMessageInputFocused;

      if (areMessagesLoaded !== undefined) scrollReadyState.areMessagesLoaded = areMessagesLoaded;

      // scroll if messages are loaded and the input is focused
      if (scrollReadyState.areMessagesLoaded && scrollReadyState.isMessageInputFocused) {
        // Wait a bit for the DOM to settle after messages render, then scroll
        setTimeout(() => {
          inputRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }, 100);

        // reset scroll ready state
        scrollReadyState.isMessageInputFocused = false;
      }
    },
    [inputRef]
  );

  useEffect(
    function handleMessagingFocus() {
      if (shouldFocusMessaging) {
        // Focus the input field using react-hook-form's built-in options
        sendMessageForm.setFocus('message', {
          shouldSelect: false
        });

        scrollToMessageInput({ isMessageInputFocused: true });

        // Reset focus state
        ticketsStore.messagingFocusMap.set(ticket.id, false);
      }
    },
    [shouldFocusMessaging, sendMessageForm, ticket.id, ticketsStore, scrollToMessageInput]
  );

  return scrollToMessageInput;
};
